import {
  assertSiteIdempotencyKey,
  SiteCommand,
  SiteCreateReportInput,
  SiteReportDraft,
  siteHttpCommand,
} from '@epc/contracts';
import { z } from 'zod';
import {
  type LocalSiteReport,
  type OfflineScope,
  type QueuedCommand,
  reportKey,
  SiteOfflineError,
  type SiteOfflineStore,
  scopeKey,
} from './store';

const Ack = z.strictObject({
  clientId: z.uuid(),
  clientOperationId: z.uuid(),
  reportId: z.uuid(),
  revision: z.number().int().positive().max(Number.MAX_SAFE_INTEGER),
});
const TransportResult = z.discriminatedUnion('kind', [
  z.strictObject({ kind: z.literal('sent'), ack: Ack, snapshot: z.unknown() }),
  z.strictObject({
    kind: z.literal('conflict'),
    revision: z.number().int().positive().max(Number.MAX_SAFE_INTEGER),
    state: z.enum(['draft', 'submitted', 'verified', 'returned', 'rejected', 'accepted']),
    server: z.unknown(),
  }),
  z.strictObject({ kind: z.literal('rejected'), code: z.string().min(1), message: z.string().min(1) }),
  z.strictObject({ kind: z.literal('auth_required') }),
  z.strictObject({ kind: z.literal('retry'), retryAfterMs: z.number().finite().nonnegative().optional() }),
]);
export type SiteTransportResult = z.infer<typeof TransportResult>;
export interface SiteTransport {
  /** Реальный HTTP-адаптер через общий API-client подключает поток 1/6 после согласования envelope. */
  send(request: {
    method: 'POST' | 'PATCH';
    path: string;
    idempotencyKey: string;
    requestJson: string;
  }): Promise<unknown>;
}

function required(row: LocalSiteReport | null): LocalSiteReport {
  if (!row) throw new SiteOfflineError('not_found', 'Черновик не найден');
  return row;
}
const active = (command: QueuedCommand) => command.state !== 'sent' && !command.resolvedBy;

export async function saveSiteDraft(
  store: SiteOfflineStore,
  scope: OfflineScope,
  clientId: string,
  draft: unknown,
  expectedLocalRevision: number,
  now: number,
): Promise<number> {
  z.uuid().parse(clientId);
  return store.mutate(scope, clientId, (existing) => {
    const row: LocalSiteReport = existing ?? {
      key: reportKey(scope, clientId),
      scope: scopeKey(scope),
      schemaVersion: 1,
      tenantId: scope.tenantId,
      userId: scope.userId,
      deviceId: scope.deviceId,
      clientId: clientId,
      localRevision: 0,
      revision: 0,
      syncedLocalRevision: 0,
      serverId: null,
      serverSnapshot: null,
      draft: null,
      savedAt: now,
      authPaused: false,
      commands: [],
      photos: [],
    };
    if (row.localRevision !== expectedLocalRevision)
      throw new SiteOfflineError('local_conflict', 'Черновик уже изменён в другой вкладке');
    // Проверять фактическую запись в транзакции: другая вкладка могла уже отправить отчёт.
    if (
      row.commands.some(
        (command) => active(command) && command.state === 'pending' && command.action === 'submit',
      )
    )
      throw new SiteOfflineError('submit_pending', 'Дождитесь результата отправки на проверку');
    const state = (row.serverSnapshot as { state?: string } | null)?.state;
    if (state && !['draft', 'returned'].includes(state))
      throw new SiteOfflineError('closed', 'Этот отчёт уже закрыт для изменения');
    row.draft = structuredClone(draft);
    row.localRevision += 1;
    row.savedAt = now;
    return { row, result: row.localRevision };
  });
}

/** Снимок фиксируется один раз; новый ввод продолжает сохраняться независимо от отправки. */
export async function enqueueSiteCommand(
  store: SiteOfflineStore,
  scope: OfflineScope,
  clientId: string,
  kind: 'create' | 'action',
  input: unknown,
  expectedLocalRevision: number,
  idempotencyKey: string,
  now: number = Date.now(),
  replacesRejectedKey?: string,
): Promise<void> {
  const command = kind === 'create' ? SiteCreateReportInput.parse(input) : SiteCommand.parse(input);
  assertSiteIdempotencyKey(command.clientOperationId, idempotencyKey);
  if (kind === 'action' && !['save', 'submit'].includes((command as SiteCommand).payload.action))
    throw new SiteOfflineError('online_only', 'Проверка и принятие выполняются онлайн');
  if (command.clientId !== clientId)
    throw new SiteOfflineError('invalid_input', 'Команда относится к другому черновику');
  await store.mutate(scope, clientId, (existing) => {
    const row = required(existing);
    if (kind === 'action' && !row.serverId)
      throw new SiteOfflineError('revision_conflict', 'Сначала создайте серверный отчёт');
    const http =
      kind === 'create'
        ? { method: 'POST' as const, path: '/api/v1/site/reports', body: command }
        : siteHttpCommand(command as SiteCommand, row.serverId!);
    const requestJson = JSON.stringify(http.body);
    const duplicate = row.commands.find((x) => x.clientOperationId === command.clientOperationId);
    if (duplicate) {
      if (duplicate.requestJson !== requestJson || duplicate.kind !== kind)
        throw new SiteOfflineError('idempotency_conflict', 'Ключ уже использован для другой команды');
      return { row, result: undefined };
    }
    if (row.localRevision !== expectedLocalRevision)
      throw new SiteOfflineError('local_conflict', 'Сначала перечитайте локальный черновик');
    if (replacesRejectedKey) {
      const rejected = row.commands.find((x) => x.clientOperationId === replacesRejectedKey);
      if (rejected?.state !== 'rejected' || rejected.resolvedBy)
        throw new SiteOfflineError('invalid_state', 'Нет отклонённой команды для исправления');
      rejected.resolvedBy = command.clientOperationId;
    }
    if (row.commands.some(active))
      throw new SiteOfflineError(
        'queue_busy',
        'Дождитесь отправки или разрешите конфликт предыдущей команды',
      );
    if (
      row.revision !== command.expectedRevision ||
      (kind === 'create' ? row.serverId !== null : row.serverId === null)
    )
      throw new SiteOfflineError('revision_conflict', 'Команда использует устаревшую серверную версию');
    const action =
      kind === 'create' ? 'create' : ((command as SiteCommand).payload.action as 'save' | 'submit');
    const snapshot =
      kind === 'create'
        ? (command as z.infer<typeof SiteCreateReportInput>).draft
        : (command as SiteCommand).payload.action === 'save'
          ? ((command as SiteCommand).payload as { action: 'save'; draft: SiteReportDraft }).draft
          : null;
    if (snapshot) {
      row.draft = structuredClone(snapshot);
      row.localRevision += 1;
      row.savedAt = now;
    }
    if (command.effectiveAt !== SiteReportDraft.parse(snapshot ?? row.draft).reportDay)
      throw new SiteOfflineError('invalid_input', 'Дата команды не совпадает со сменой');
    const requiredPhotos: QueuedCommand['requiredPhotos'] = [];
    if (action === 'submit') {
      if (row.syncedLocalRevision !== row.localRevision)
        throw new SiteOfflineError('unsent_draft', 'Сначала сохраните текущую версию на сервере');
      const draft = SiteReportDraft.parse(row.draft);
      for (const photo of draft.photos) {
        const local = row.photos.find((x) => x.clientPhotoId === photo.clientPhotoId);
        if (
          !photo.fileId ||
          (local &&
            (local.state !== 'sent' ||
              local.fileId !== photo.fileId ||
              local.sha256 !== photo.sha256 ||
              local.size !== photo.size))
        )
          throw new SiteOfflineError('photos_pending', 'Дождитесь подтверждения всех фотографий');
        requiredPhotos.push({
          clientPhotoId: photo.clientPhotoId,
          fileId: photo.fileId,
          sha256: photo.sha256,
          size: photo.size,
        });
      }
    }
    // Черновик и снимок команды записываются одним commit IndexedDB.
    row.commands.push({
      clientOperationId: command.clientOperationId,
      kind,
      action,
      method: http.method,
      path: http.path,
      requiredPhotos,
      requestJson,
      expectedRevision: command.expectedRevision,
      localRevision: row.localRevision,
      state: 'pending',
      attempts: 0,
      nextAttemptAt: 0,
      lease: null,
      error: null,
      conflict: null,
      resolvedBy: null,
      ack: null,
    });
    return { row, result: undefined };
  });
}

export async function resumeSiteSession(store: SiteOfflineStore, scope: OfflineScope, clientId: string) {
  await store.mutate(scope, clientId, (existing) => {
    const row = required(existing);
    row.authPaused = false;
    return { row, result: undefined };
  });
}

/** Один запрос за вызов. Запуск на online/focus и таймер повторов принадлежит экрану site. */
export async function syncSiteOnce(
  store: SiteOfflineStore,
  scope: OfflineScope,
  clientId: string,
  transport: SiteTransport,
  now: () => number = Date.now,
): Promise<'idle' | 'processed'> {
  const token = crypto.randomUUID();
  const claimed = await store.mutate(scope, clientId, (existing) => {
    const row = required(existing);
    const command = row.commands.find(active);
    if (
      !command ||
      row.authPaused ||
      command.state !== 'pending' ||
      command.nextAttemptAt > now() ||
      (command.lease && command.lease.until > now())
    )
      return { row, result: null };
    // Отправка не обгоняет незавершённые фото. Сам транспорт не загружает файлы в обход файлового API.
    if (
      command.requiredPhotos.some((p) => {
        const local = row.photos.find((x) => x.clientPhotoId === p.clientPhotoId);
        return (
          local &&
          (local.state !== 'sent' ||
            local.fileId !== p.fileId ||
            local.sha256 !== p.sha256 ||
            local.size !== p.size)
        );
      })
    )
      return { row, result: null };
    command.lease = { token, until: now() + 30_000 };
    command.attempts += 1;
    return { row, result: { command: structuredClone(command), serverId: row.serverId } };
  });
  if (!claimed) return 'idle';
  let result: SiteTransportResult;
  try {
    result = TransportResult.parse(
      await transport.send({
        method: claimed.command.method,
        path: claimed.command.path,
        idempotencyKey: claimed.command.clientOperationId,
        requestJson: claimed.command.requestJson,
      }),
    );
  } catch {
    result = { kind: 'retry' };
  }
  await store.mutate(scope, clientId, (existing) => {
    const row = required(existing);
    const command = row.commands.find((x) => x.clientOperationId === claimed.command.clientOperationId)!;
    // Просроченный отправитель не может переписать результат новой попытки.
    if (command.lease?.token !== token) return { row, result: undefined };
    command.lease = null;
    if (result.kind === 'sent') {
      const parsed = Ack.safeParse(result.ack);
      if (
        !parsed.success ||
        parsed.data.clientId !== clientId ||
        parsed.data.clientOperationId !== command.clientOperationId ||
        parsed.data.revision !== command.expectedRevision + 1 ||
        (row.serverId && parsed.data.reportId !== row.serverId)
      ) {
        result = { kind: 'retry' };
        command.error = {
          code: 'invalid_ack',
          message: 'Сервер вернул неподходящее подтверждение; команда сохранена',
        };
      } else {
        command.state = 'sent';
        command.ack = { reportId: parsed.data.reportId, revision: parsed.data.revision };
        command.error = null;
        row.revision = parsed.data.revision;
        row.serverId = parsed.data.reportId;
        row.serverSnapshot = structuredClone(result.snapshot);
        if (command.action === 'create' || command.action === 'save')
          row.syncedLocalRevision = command.localRevision;
      }
    }
    if (result.kind === 'conflict') {
      command.state = 'conflict';
      command.conflict = {
        revision: result.revision,
        state: result.state,
        server: structuredClone(result.server),
        base: structuredClone(row.serverSnapshot),
      };
      command.error = { code: 'revision_conflict', message: 'Сравните локальную и серверную версии' };
    } else if (result.kind === 'rejected') {
      command.state = 'rejected';
      command.error = { code: result.code, message: result.message };
    } else if (result.kind === 'auth_required') {
      row.authPaused = true;
      command.error = { code: 'auth_required', message: 'Войдите снова тем же пользователем' };
    } else if (result.kind === 'retry') {
      const delay = result.retryAfterMs ?? Math.min(60_000, 1_000 * 2 ** Math.min(command.attempts - 1, 6));
      command.nextAttemptAt = now() + Math.min(86_400_000, Math.max(1_000, delay));
    }
    return { row, result: undefined };
  });
  return 'processed';
}

/** Пользователь выбрал объединённую версию. Старый запрос и обе версии конфликта остаются в истории. */
export async function resolveSiteConflict(
  store: SiteOfflineStore,
  scope: OfflineScope,
  clientId: string,
  key: string,
  newKey: string,
  chosen: SiteReportDraft,
  sourceRevisions: SiteCommand['sourceRevisions'],
  expectedLocalRevision: number,
  now: number,
) {
  const draft = SiteReportDraft.parse(chosen);
  z.uuid().parse(newKey);
  await store.mutate(scope, clientId, (existing) => {
    const row = required(existing);
    const command = row.commands.find((x) => x.clientOperationId === key);
    if (command?.state !== 'conflict' || !command.conflict || command.resolvedBy)
      throw new SiteOfflineError('invalid_state', 'Нет неразрешённого конфликта');
    if (!['draft', 'returned'].includes(command.conflict.state) || !row.serverId)
      throw new SiteOfflineError('invalid_state', 'Серверный отчёт закрыт для правки');
    if (row.commands.some((x) => x.clientOperationId === newKey))
      throw new SiteOfflineError('idempotency_conflict', 'Для решения нужен новый ключ');
    if (row.localRevision !== expectedLocalRevision)
      throw new SiteOfflineError('local_conflict', 'Черновик уже изменён в другой вкладке');
    const request = SiteCommand.parse({
      schemaVersion: 1,
      clientId: clientId,
      clientOperationId: newKey,
      expectedRevision: command.conflict.revision,
      recordedOffline: false,
      sourceRevisions,
      occurredAt: new Date(now).toISOString(),
      effectiveAt: draft.reportDay,
      payload: { action: 'save', draft },
    });
    const http = siteHttpCommand(request, row.serverId);
    command.resolvedBy = newKey;
    row.revision = command.conflict.revision;
    row.serverSnapshot = structuredClone(command.conflict.server);
    row.draft = draft;
    row.localRevision += 1;
    row.savedAt = now;
    row.commands.push({
      clientOperationId: newKey,
      kind: 'action',
      action: 'save',
      method: http.method,
      path: http.path,
      requiredPhotos: [],
      requestJson: JSON.stringify(http.body),
      expectedRevision: request.expectedRevision,
      localRevision: row.localRevision,
      state: 'pending',
      attempts: 0,
      nextAttemptAt: 0,
      lease: null,
      error: null,
      conflict: null,
      resolvedBy: null,
      ack: null,
    });
    return { row, result: undefined };
  });
}
